import PAYRIX_CONFIG from './payrix-payments-variables';
import Alert from '@/lib/alert';
import * as payments from '@/rest/payrix';
import store from '@/store/store';
import util from '@/lib/util';
import PaymentBankAccount from '../types/PaymentBankAccount';
import PaymentCreditCard from '../types/PaymentCreditCard';
import paymentApi, {ProcessorName} from '../paymentApi';
import logo from './OutlawPayments.png';
import {getCurrentFirm, getCurrentFirmId} from "@/lib/picos/currentLogin";
import {
    BankAccounts,
    BankPaymentMethodNames,
    CardPaymentMethodNames,
    EAccountStatus,
    EBankPaymentMethodNames,
    ECardPaymentMethodNames,
    EPaymentMethods,
    EPaymentType,
    ETransactionStatus,
    ETransactionStatusName,
    ETransactionType,
    ETransactionTypeName,
    PaymentMethods,
    PayrixAddress,
    PayrixEntity,
    PayrixPaymentMeta,
    PayrixPaymentMethod,
    PayrixToken,
    PayrixTokenCustom,
    PayrixTransaction,
    PayrixTransactionFull,
    PayrixTxnCustom,
    PayrixTxnResult,
    ZPayrixPaymentMeta
} from "@/@types/payrix";
import Contact from "@/entities/contact";
import Address from '@/entities/address';
import Transaction from '@/entities/transaction';
import {PaymentMethod, StandardAccount} from "@/@types/payments";
import {Err, Ok, Result} from "oxide.ts";
import Icons, {IconType} from "@/ui/IconButton/Icons";
import Matter from '@/entities/matter';
import Lead from "@/entities/lead";
import PaymentMethodBase from "../types/BasePaymentMethod";
import {safeParse} from "@/lib/picos/safeParse";
import {normalizePaymentMeta} from "../normalizePaymentMeta";
import {toTwoDecimals} from "@/lib/picos/numberFormat";
import {convertEasternToUTC} from "@/lib/dateutils";
import {snakeToText} from "@/lib/picos/textUtils";
import decodeError from "./payrixErrors";
import {getZeroUUID} from "@/lib/picos/uuids";
import {uuid} from "@/@types/entity";
import * as z from 'zod';

export type Flags = 'CHARGE' | 'REFUND' | 'INCOMPLETE' | 'COMPLETE' | 'VOID' | 'FAILED';

export const PaymentMethodNames: (CardPaymentMethodNames | BankPaymentMethodNames | null)[] = [
    null,
    ECardPaymentMethodNames.AmericanExpress,
    ECardPaymentMethodNames.Visa,
    ECardPaymentMethodNames.Mastercard,
    ECardPaymentMethodNames.DinersClub,
    ECardPaymentMethodNames.Discover,
    null,
    null,
    EBankPaymentMethodNames.IndividualChecking,
    EBankPaymentMethodNames.IndividualSavings,
    EBankPaymentMethodNames.BusinessChecking,
    EBankPaymentMethodNames.BusinessSavings
];

export const TxnResultCodeNames = [
    "Transaction approved",
    "The processor has only approved a portion of the total transaction amount",
    "The processor has declined the transaction",
    "Verification successful, values provided matched",
    "Verification unsuccessful, values provided did not match",
    "The ZIP code in the Transaction data does not match the customer details held by the card issuer",
    "The address in the Transaction data does not match the customer details held by the card issuer",
    "The name in the Transaction data does not match the customer details held by the card issuer",
    "The name and phone number in the Transaction data do not match the details held by the card issuer",
    "The name and email address in the Transaction data do not match the customer details held by the card issuer",
    "The phone number in the Transaction data does not match the customer details held by the card issuer",
    "The phone number and email address in the Transaction data do not match the customer details held by the card issuer",
    "The email address in the Transaction data does not match the customer details held by the card issuer",
    "The customer name could not be found in the Transaction data",
    "The customer name and phone number were not found in the Transaction data",
    "The customer name and email address were not found in the Transaction data",
    "The customer phone number was not found in the Transaction data",
    "The customer phone number and email address were not found in the Transaction data",
    "The customer email address was not found in the Transaction data",
    "Information about the customer was not found in the Transaction data",
    "Non-sufficient funds. The customer did not have sufficient credit or balance to cover the Transaction",
    "The account in the Transaction data is not valid",
    "The account is unauthorized",
    "General error",
    "The ZIP code in the Transaction data was not verified",
    "The ZIP code and the Address in the Transaction data were not verified",
    "The Address in the Transaction data was not verified",
    "Txn did not fail but was not captured",
    "3DS authentication passed",
    "3DS authentication is invalid",
    "3DS authentication failed",
    "3DS authentication was not validated",
    "3DS authentication passed but liability was not shifted",
];

export const TxnResultTypes = [
    '', // 0
    'General', // 1...
    'Fraud Prevention Alert',
    'Processor',
    'CVV Mismatch',
    'AVS Check Alert',
    'AAVS Check Alert',
    'Network Error',
    '3DS Check Alert'
];

export function parseTxnResult(txnResult: PayrixTxnResult) {
    return {
        code: TxnResultCodeNames[txnResult.code],
        type: TxnResultTypes[txnResult.type],
        bankCode: snakeToText(txnResult.bankCode ?? ''),
        message: decodeError(txnResult.bankCode ?? '', txnResult.message as string),
    }
}

export type TxnResultParsed = {
    code: string,
    type: string,
    bankCode: string,
    message: string
};

export function getBankTypeName(type: PaymentMethods): Result<BankPaymentMethodNames, string> {
    switch (type) {
        case EPaymentMethods.CheckingAccount:
            return Ok(EBankPaymentMethodNames.IndividualChecking);
        case EPaymentMethods.SavingsAccount:
            return Ok(EBankPaymentMethodNames.IndividualSavings);
        case EPaymentMethods.CorporateCheckingAccount:
            return Ok(EBankPaymentMethodNames.BusinessChecking);
        case EPaymentMethods.CorporateSavingsAccount:
            return Ok(EBankPaymentMethodNames.BusinessSavings);
        default:
            return Err(`Bank payment method type is not recognized: ${type}. Expecting one of 7 , 8, 10 or 11`);
    }
}

export function getCardTypeName(cardType: PaymentMethods): Result<ECardPaymentMethodNames, string> {
    switch (cardType) {
        case EPaymentMethods.Visa:
            return Ok(ECardPaymentMethodNames.Visa);
        case EPaymentMethods.Mastercard:
            return Ok(ECardPaymentMethodNames.Mastercard);
        case EPaymentMethods.AmericanExpress:
            return Ok(ECardPaymentMethodNames.AmericanExpress);
        case EPaymentMethods.Discover:
            return Ok(ECardPaymentMethodNames.Discover);
        case EPaymentMethods.DinersClub:
            return Ok(ECardPaymentMethodNames.DinersClub);
        default:
            return Err(`Card payment method is not recognized: ${cardType}. Expecting one of 1, 2, 3 or 5 (4 was Diner's Club, bought by Discover so now 5)`);
    }
}

export function getStatusText(status: ETransactionStatus) {
    switch (status) {
        case ETransactionStatus.Pending:
            return ETransactionStatusName.Pending;
        case ETransactionStatus.Approved:
            return ETransactionStatusName.Approved;
        case ETransactionStatus.Failed:
            return ETransactionStatusName.Failed;
        case ETransactionStatus.Captured:
            return ETransactionStatusName.Captured;
        case ETransactionStatus.Settled:
            return ETransactionStatusName.Settled;
        case ETransactionStatus.Returned:
            return ETransactionStatusName.Returned;
    }
}

export function getTxnDescription(txn: PayrixTransaction | PayrixTransactionFull) {
    // safeParse passes its arg straight to JSON.parse (try/catch); null yields null,
    // '' would throw → {}, so cast to preserve the exact existing behavior.
    return safeParse<PayrixTxnCustom>(txn.description as string);
}

export function getTokenCustom(token: PayrixToken) {
    return safeParse<PayrixTokenCustom>(token.custom as string);
}

export function getEventName(event: number): string {
    const lookup: Array<[number, string]> = [
        [1, "Daily event"],
        [2, "Weekly event"],
        [3, "Monthly event"],
        [4, "Annual event"],
        [5, "One-off event"],
        [6, "Transaction has been authorized"],
        [7, "Transaction has been captured"],
        [8, "Refund transaction is processed"],
        [9, "Merchant is boarded"],
        [10, "Payout is processed"],
        [11, "Card chargeback occurs"],
        [12, "Overdraft usage charge from a bank is levied"],
        [13, "Interchange Fees are assessed for the Transactions of this Merchant"],
        [14, "The Transactions of this Merchant are processed by a payment processor"],
        [15, "Automated clearing house failure occurs"],
        [16, "Bank account verification"],
        [17, "Transaction's fraud Score"],
        [18, "Adjustment"],
        [19, "Retrieval request chargeback"],
        [20, "Arbitration chargeback"],
        [21, "eCheck sale transaction"],
        [22, "eCheck refund"],
        [23, "eCheck return"],
        [24, "Transaction has been settled"],
        [25, "There's been a Misuse of authorization"],
        [26, "Profit sharing entry event"],
        [27, "Transaction is unauthorized"],
        [28, "Disbursement NOC (Notification of change)"],
        [29, "eCheck transaction NOC (Notification of change)"],
        [30, "eCheck transaction failed"],
        [31, "eCheck transaction NSF (Non-sufficient funds) return"],
        [32, "Currency conversion"],
        [33, "Terminal transaction"],
        [34, "Payout that has been reversed"],
        [35, "Payout that has been partially reversed"],
        [43, "Payment check for Account Updater"],
        [44, "Payment update for Account Updater"],
        [45, "Payment group check for Account Updater"],
        [46, "Payment group update for Account Updater"],
        [47, "Refund"],
    ];

    let item = lookup.find((item: [number, string]) => item[0] === event);
    return item?.[1] || `Event not found: ${event}`;
}

export function isVoided(txn: PayrixTransactionFull) {
    // We need a transaction with an empty batch
    return txn.batch === null;
}

export function isRefunded(txn: PayrixTransactionFull) {
    // We need a transaction status showing a refund
    return [ETransactionTypeName.ECheckRefund, ETransactionType.CreditCardRefund].includes(txn.type);
}

/**
 * Is the given payment method associated with a credit card?
 *
 * @param {number} paymentMethod
 * @returns {boolean}
 */
export function isCreditCard(paymentMethod: EPaymentMethods): boolean {
    return paymentMethod >= 1 && paymentMethod <= 5;
}

/**
 * Is the given payment method associated with a bank account?
 *
 * @param {number} paymentMethod
 * @returns {boolean}
 */
export function isBankAccount(paymentMethod: number): boolean {
    return paymentMethod >= 8 && paymentMethod <= 11;
}

/**
 * Figure out the card type based on the number.
 *
 * @param {string} cardNumber
 * @returns {{type: number, label: string, icon: IconType}}
 */
export function getCardType(cardNumber: string): { type: number, label: string, icon: IconType } {
    const amexPattern = /^3[47]\d{13}$/;
    const dinersClubPattern = /^3(?:0[0-5]|[68]\d)\d{11}$/;
    const visaPattern = /^4\d{15}$|^4\d{12}$/; // Split into two alternatives instead of optional group
    const mastercardPattern = /^(?:5[1-5]\d{2}|222[1-9]|22[3-9]\d|2[3-6]\d{2}|27[01]\d|2720)\d{12}$/;
    const mastercardRelaxedPattern = /^5[1-5]\d{14}$/;
    const discoverPattern = /^6(?:011|5\d{2})\d{12}$/;

    // Cards we do not cover - also made safer
    const BCGlobal = /^(?:6541|6556)\d{12}$/;
    const CarteBlanche = /^389\d{11}$/;
    const InstaPayment = /^63[7-9]\d{13}$/;
    const JCB = /^(?:2131|1800|35\d{3})\d{11}$/;
    const KoreanLocal = /^9\d{15}$/;
    const LaserCard = /^(?:6304|6706|6709|6771)\d{12,15}$/;
    const MaestroCard = /^(?:5018|5020|5038|5893|6304|6759|6761|6762|6763)\d{8,15}$/;
    const SoloCard = /^(?:6334|6767)\d{12}$|^(?:6334|6767)\d{14}$|^(?:6334|6767)\d{15}$/;
    const SwitchCard = /^(?:4903|4905|4911|4936|6333|6759)\d{12}$|^(?:4903|4905|4911|4936|6333|6759)\d{14}$|^(?:4903|4905|4911|4936|6333|6759)\d{15}$|^564182\d{10}$|^564182\d{12}$|^564182\d{13}$|^633110\d{10}$|^633110\d{12}$|^633110\d{13}$/;
    const UnionPay = /^62\d{14,17}$/;

    if (visaPattern.test(cardNumber)) {
        return {type: EPaymentMethods.Visa, label: 'Visa', icon: Icons.CC_VISA};
    }
    else if (mastercardPattern.test(cardNumber) || mastercardRelaxedPattern.test(cardNumber)) {
        return {type: EPaymentMethods.Mastercard, label: 'Mastercard', icon: Icons.CC_MASTERCARD};
    }
    else if (amexPattern.test(cardNumber)) {
        return {type: EPaymentMethods.AmericanExpress, label: 'American Express', icon: Icons.CC_AMEX};
    }
    else if (discoverPattern.test(cardNumber)) {
        return {type: EPaymentMethods.Discover, label: 'Discover', icon: Icons.CC_DISCOVER};
    }
    else if (dinersClubPattern.test(cardNumber)) {
        return {type: EPaymentMethods.DinersClub, label: 'Diners Club', icon: Icons.CC_DINERS_CLUB};
    }
    else if (BCGlobal.test(cardNumber)) throw new Error(`This appears to be a BCGlobal card, which Outlaw Payments does not support.`)
    else if (CarteBlanche.test(cardNumber)) throw new Error(`This appears to be a CarteBlanche card, which Outlaw Payments does not support.`)
    else if (InstaPayment.test(cardNumber)) throw new Error(`This appears to be a InstaPayment card, which Outlaw Payments does not support.`)
    else if (JCB.test(cardNumber)) throw new Error(`This appears to be a JCB card, which Outlaw Payments does not support.`)
    else if (KoreanLocal.test(cardNumber)) throw new Error(`This appears to be a KoreanLocal card, which Outlaw Payments does not support.`)
    else if (LaserCard.test(cardNumber)) throw new Error(`This appears to be a LaserCard, which Outlaw Payments does not support.`)
    else if (MaestroCard.test(cardNumber)) throw new Error(`This appears to be a MaestroCard, which Outlaw Payments does not support.`)
    else if (SoloCard.test(cardNumber)) throw new Error(`This appears to be a SoloCard, which Outlaw Payments does not support.`)
    else if (SwitchCard.test(cardNumber)) throw new Error(`This appears to be a SwitchCard, which Outlaw Payments does not support.`)
    else if (UnionPay.test(cardNumber)) throw new Error(`This appears to be a UnionPay card, which Outlaw Payments does not support.`)
    else throw new Error('This is not a valid card number for the payment cards that Outlaw Payments supports.');
}

/**
 * Class to handle LawPay transactions
 */
class Payrix {

    /**
     * Are we currently in the process of connecting?
     */
    static isConnecting = false;

    /**
     * Did we get the code?
     */
    static gotCode = false;

    /**
     * The LawPay icon.
     */
    static getIcon(): string {
        return logo;
    }

    /**
     * The brand name.
     */
    static getName() {
        return 'Outlaw Payments';
    }

    /**
     * There is no connect method for Outlaw Payments, just signup.
     */
    static async connect() {
        // OWA-734: an existing merchant is not an approval. OWA-1112: paymentProcessor is
        // server-owned, so the server makes the fresh status read and connects only when the
        // merchant can take payments now. Its refusals carry the text to show. The updated
        // firm arrives over the socket.
        try {
            await payments.connect();
            Alert.addSimpleSuccess('Your Outlaw Payments account has been connected.');
        }
        catch (err) {
            Alert.addSimpleError(err?.message || 'We could not check your Outlaw Payments account, so it was not connected. ' +
                                                 'Try again in a few minutes.');
        }
    }

    /**
     * Start the OAuth connection flow for existing accounts.
     * @return {Promise<{applicationId: string, redirect_uri: string}>}
     */
    static async signup(): Promise<{ applicationId: string; redirect_uri: string | null; }> {
        // Need the current address, phone number, etc for the owner
        const ownerIds = store.getState().roles.find(r => r.name === 'Owner')!.users;

        const ownerUsers = store.getState().users.filter(u => ownerIds.includes(u.id));

        const owners = ownerUsers.map(owner => {
            const ownerContact = store.getState().contacts.find(c => owner.contactId === c.id)!;
            const email = store.getState().resources.find(r => r.entityId === ownerContact.id && r.type === 'email' && r.isPrimary);
            const phone = store.getState().resources.find(r => r.entityId === ownerContact.id && r.type === 'phone' && r.isPrimary);
            const website = store.getState().resources.find(r => r.entityId === ownerContact.id && r.type === 'web' && r.isPrimary);
            const address = store.getState().addresses.find(r => r.entityId === ownerContact.id && r.isPrimary);

            return {
                owner,
                contact: ownerContact,
                email: email?.value || '',
                phone: phone?.value?.replace(/[^0-9.]/g, '') || '',
                website: website?.value || '',
                address
            }
        });

        const primaryOwner = owners[0];
        const secondaryOwner = owners[1];

        const numEmployees = store.getState().users.filter(u => u.enabled).length - owners.length;
        const hasEmployees = numEmployees > 0;

        const name = getCurrentFirm().legalName;
        const type = name.includes('LLC') || name.includes('LLP') ? 2 : null;

        let businessAddress: Address | null | undefined, businessPhone: string | undefined;

        const firmContact = store.getState().contacts.find(c => c.tags?.includes('TheFirm'));

        if (firmContact) {
            businessAddress = store.getState().addresses.find(a => a.entityId === firmContact.id && a.label === 'business');
            businessPhone = store.getState().resources.find(r => r.entityId === firmContact.id && r.type === 'phone' && r.label === 'business')?.value;
        }

        // Redirect to the Payrix application.  We will need to check occasionally to see if the merchant has
        // successfully onboarded, or if there is an issue.
        const values = new Map();
        values.set('country', 'USA');
        values.set('name', name.replaceAll('&', 'and'));
        if (type) values.set('type', type);
        values.set('phone', businessPhone || primaryOwner.phone || '');
        values.set('customerPhone', businessPhone || primaryOwner.phone || '');
        values.set('email', primaryOwner.email);
        values.set('haveEmployees', hasEmployees ? 'true' : 'false');

        if (businessAddress) {
            values.set('address1', businessAddress.street || '');
            values.set('address2', businessAddress.unit || '');
            values.set('city', businessAddress.city || '');
            values.set('state', businessAddress.state || '');
            values.set('zip', businessAddress.zip || '');
        }

        values.set('merchant[dba]', getCurrentFirm().legalName.replaceAll('&', 'and'));
        values.set('merchant[environment]', 'moto');
        values.set('merchant[industry]', '8111');
        values.set('merchant[advancedBilling]', true);
        values.set('merchant[custom]', getCurrentFirmId());

        /* Set up the first owner */
        values.set('merchant[members][0][first]', primaryOwner.contact.first || '');
        values.set('merchant[members][0][middle]', primaryOwner.contact.middle || '');
        values.set('merchant[members][0][last]', primaryOwner.contact.last || '');
        values.set('merchant[members][0][significantResponsibility]', true);
        values.set('merchant[members][0][email]', primaryOwner.email || primaryOwner.owner.email || '');
        values.set('merchant[members][0][phone]', primaryOwner.phone || '');

        if (primaryOwner.address) {
            values.set('merchant[members][0][address1]', primaryOwner.address.street || '');
            values.set('merchant[members][0][address2]', primaryOwner.address.unit || '');
            values.set('merchant[members][0][city]', primaryOwner.address.city || '');
            values.set('merchant[members][0][state]', primaryOwner.address.state || '');
            values.set('merchant[members][0][zip]', primaryOwner.address.zip || '');
            values.set('merchant[members][0][country]', primaryOwner.address.country || 'USA');
        }

        /* Set up a second owner, if applicable */
        if (secondaryOwner) {
            values.set('merchant[members][1][first]', secondaryOwner.contact.first || '');
            values.set('merchant[members][1][middle]', secondaryOwner.contact.middle || '');
            values.set('merchant[members][1][last]', secondaryOwner.contact.last || '');
            values.set('merchant[members][1][email]', secondaryOwner.email || secondaryOwner.owner.email || '');
            values.set('merchant[members][1][phone]', secondaryOwner.phone || '');

            if (secondaryOwner.address) {
                values.set('merchant[members][1][address1]', secondaryOwner.address.street || '');
                values.set('merchant[members][1][address2]', secondaryOwner.address.unit || '');
                values.set('merchant[members][1][city]', secondaryOwner.address.city || '');
                values.set('merchant[members][1][state]', secondaryOwner.address.state || '');
                values.set('merchant[members][1][zip]', secondaryOwner.address.zip || '');
                values.set('merchant[members][1][country]', secondaryOwner.address.country || 'USA');
            }
        }

        let href = `${PAYRIX_CONFIG!.signup_uri}?`;
        let i = 0;
        for (let key of values.keys()) {
            if (i++ > 0) href += '&';
            href += `${key}=${encodeURIComponent(values.get(key))}`;
        }

        window.open(href, '_blank', 'popup');

        return {
            applicationId: getCurrentFirmId(),
            redirect_uri: null
        };
    }

    /**
     * Switch Outlaw Payments off. OWA-1112: paymentProcessor is server-owned, so the server clears
     * it; the updated firm arrives over the socket.
     *
     * @throws {Error} when the server did not disconnect (after telling the user)
     */
    static async disconnect(): Promise<void> {
        try {
            await payments.disconnect();
        }
        catch (err) {
            Alert.addSimpleError('Outlaw Payments was not disconnected: ' + err?.message);
            throw err;
        }
    }

    static async getContacts(): Promise<Contact[]> {
        try {
            return await payments.getContacts();
        }
        catch (err) {
            Alert.add(new Alert(Alert.ERROR, 0, 0, 'Error getting contacts from LawPay: ' + err.message));
        }
        // Error path falls through to undefined; preserve that (the Promise<Contact[]>
        // signature is a known lie — callers go through paymentApi which isn't yet
        // strict-null migrated).
        return undefined as unknown as Contact[];
    }

    /**
     * Create a contact using the LawPay Contact api
     * @param {Contact[]} _
     * @returns {Promise<void>}
     */
    static async syncContacts(_: Contact[]): Promise<void> {
        // Nothing to do here, as we do not sync contacts
    }

    /**
     * Delete a contact using their id
     * @param {string} processorContactId
     */
    static async deleteContact(processorContactId: string) {
        return payments.deleteContact(processorContactId);
    }

    /**
     * Do we have a valid access token?
     */
    static isConnected(): boolean {
        return paymentApi.isConnected(ProcessorName.Payrix);
    }

    static accountStatusLabel(status: EAccountStatus): string {
        switch (status) {
            case EAccountStatus.NotReady: return 'Not Ready';
            case EAccountStatus.Ready: return 'Ready';
            case EAccountStatus.Challenged: return 'Challenged';
            case EAccountStatus.Verified: return 'Verified';
            case EAccountStatus.Manual: return 'Manual Review';
            default: return 'Unknown';
        }
    }

    /**
     * Get operating and IOLTA accounts from the server.
     */
    static async getAccounts(): Promise<StandardAccount[]> {
        // The null/undefined returns below are intentional (callers null-check the
        // result); the Promise<StandardAccount[]> signature is a known lie pending the
        // paymentApi strict-null migration, so cast rather than widen (avoids cascade).
        if (!store.getState().misc.isConnected) return null as unknown as StandardAccount[];

        let accounts: BankAccounts;

        try {
            accounts = await payments.getAccounts();
            if (!accounts) return null as unknown as StandardAccount[];

            const {operating, iolta} = accounts;
            const extractMask = (acct: typeof operating) =>
                acct.mask ?? acct.account?.number?.replace(/\D/g, '').slice(-4) ?? undefined;

            return [
                {
                    theirId: operating.id!,
                    name: operating.name,
                    accountType: 'Merchant Account',
                    trustAccount: false,
                    currency: operating.currency,
                    mask: extractMask(operating),
                    payrixType: 'operating',
                    inactive: operating.inactive === 1,
                    status: Payrix.accountStatusLabel(operating.status),
                },
                {
                    theirId: iolta.id!,
                    name: iolta.name,
                    accountType: 'IOLTA',
                    trustAccount: true,
                    currency: operating.currency,
                    mask: extractMask(iolta),
                    payrixType: 'iolta',
                    inactive: iolta.inactive === 1,
                    status: Payrix.accountStatusLabel(iolta.status),
                }
            ];
        }
        catch (err) {
            Alert.add(new Alert(Alert.ERROR, 0, 0, 'Error getting accounts from Outlaw Payments: ' + err.message));
        }
        // Error path falls through to undefined; preserve that (see signature note above).
        return undefined as unknown as StandardAccount[];
    }

    /**
     * Update a contact in Payrix with new information.
     *
     * @param {Contact} _
     * @returns {Promise<string>}
     */
    static async updateContact(_: Contact): Promise<void> {
        throw new Error('Not implemented for Payrix');
    }

    static async updateTxn(txnId: uuid, description: string): Promise<void> {
        await payments.updateTxn(txnId, description);
    }

    /**
     * Is this contact eligible to have payments?
     *
     * @param {Contact} contact
     * @param {Lead | Matter} unsavedEntity
     */
    static paymentEligible(contact: Contact, unsavedEntity: Lead | Matter): boolean {
        if (!contact) return false;
        if (!paymentApi.usesPayments()) return false;

        // Annotate as uuid[]: the .map().flat().filter() chain infers never[] under
        // strictNullChecks, which then poisons the concat below. Elements are uuids.
        const leadClientIds: uuid[] = store.getState().leads
            .map(l => l.clients.concat(l.payers))
            .flat()
            .filter(util.distinctFilter);

        const matterClientIds: uuid[] = store.getState().matters
            .map(l => l.clients)
            .flat()
            .filter(util.distinctFilter);

        const benefactorLookupId = store.getState().lookups
            .find(l => l.path === 'any:participant:relationship' && l.key === 'Benefactor')?.id;

        const benefactorIds = store.getState().participants
            .filter(p => p.relationships.includes(benefactorLookupId!))
            .map(p => p.contactId);

        const allIds = leadClientIds.concat(matterClientIds)
            .concat(benefactorIds)
            .flat()
            .filter(util.distinctFilter);

        if (unsavedEntity) allIds.push(...unsavedEntity.clients);
        if (unsavedEntity && 'payers' in unsavedEntity) allIds.push(...unsavedEntity.payers);

        const bus = contact.isCompany;

        const basics = ((bus && contact.last) ||
            (!bus && (contact.first && contact.last))) && contact.status !== 'D' && allIds.includes(contact.id);

        const hasEmail = !!store.getState().resources
            .find(r => r.entityId === contact.id && r.type === 'email' && r.isPrimary && r.canContact);

        const hasAddress = !!store.getState().addresses
            .find(r => r.entityId === contact.id && r.isPrimary && r.canContact);

        const hasPhone = !!store.getState().resources
            .find(r => r.entityId === contact.id && r.type === 'phone' && r.isPrimary && r.canContact);

        return (basics && hasEmail && hasAddress && hasPhone) as boolean;
    }

    /**
     *
     * @param {Contact} contact
     * @returns {Promise<PaymentMethod[]>}
     */
    static async getPaymentMethods(contact: Contact): Promise<PaymentMethodBase[]> {
        if (!contact) throw new TypeError('contact not provided to getPaymentMethods');

        try {
            const result = await payments.getPaymentMethods(contact);
            if (!result) {
                return [];
            }

            const paymentMethods = result.map(pm => {
                pm.contact = contact;

                if (pm.type === EPaymentType.Card) return new PaymentCreditCard(pm);
                else return new PaymentBankAccount(pm);
            });

            return paymentMethods || [];
        }
        catch (err) {
            Alert.add(new Alert(Alert.ERROR, contact.id, contact.addedOn, err.message));
            console.error(err);
            return [];
        }
    }

    /**
     * This method will create a new payment method and attach it to a contact.
     *
     * @param {Contact} contact
     * @param {paymentMethod} paymentMethod
     */
    static async createPaymentMethod(contact: Contact, paymentMethod: PaymentMethod): Promise<{ status: number, body: PayrixToken | any }> {
        // Store the payment method
        return payments.createPaymentMethod(contact, paymentMethod);
    }

    /**
     * Update the payment method.
     *
     * @param {Contact} contact
     * @param {PayrixPaymentMethod} paymentMethod
     * @returns {Promise<PaymentMethod>}
     */
    static async updatePaymentMethod(contact: Contact, paymentMethod: PaymentMethod): Promise<PayrixPaymentMethod> {
        // Store the payment method
        return payments.updatePaymentMethod(contact, paymentMethod);
    }

    /**
     * Delete the payment method.
     *
     * @param {Contact} contact
     * @param {paymentMethod} paymentMethod
     * @returns {Promise<string>}
     */
    static async deletePaymentMethod(contact: Contact, paymentMethod: PayrixPaymentMethod): Promise<void> {
        try {
            // Store the payment method
            return payments.deletePaymentMethod(contact, paymentMethod);
        }
        catch (err) {
            Alert.add(new Alert(Alert.ERROR, contact.id, contact.addedOn, err.message));
            console.error(err);
            return;
        }
    }

    /**
     * Format Outlaw address for LawPay.
     *
     * @param {Address} address
     * @returns {PayrixAddress}
     */
    static formatAddress(address: any): PayrixAddress {
        return {
            type: address.label,
            address1: address.street,
            address2: address.unit,
            city: address.city,
            state: address.state,
            zip: address.zip,
            country: address.country
        }
    }

    // /**
    //  * Format in LawPay's preferred format.
    //  *
    //  * @param {paymentMethod} paymentMethod
    //  * @returns {object}
    //  */
    // static formatPaymentMethod(paymentMethod: PaymentCreditCard | PaymentBankAccount): PayrixPaymentMethod {
    // 	if (paymentMethod.paymentType === 'card') {
    // 		const {
    // 			id,
    // 			cardType, // Card or bank method
    // 			paymentType, // Obviously "card" here. Other value is "bank"
    // 			contact, // The Contact object
    // 			accountNumber, // Account number for the card
    // 			cardCode, // 3-digit CVV (optional)
    // 			expMonth, // 2-digit month
    // 			expYear, // 4-digit year
    // 			nameOnCard, // User's name, e.g. Edward Pham
    // 			description, // User-supplied description
    // 			email, // email to send notifications to
    // 			phone, // phone number on file
    // 			address // address1, address2, city, state_or_province, postal_code, country_code
    // 		} = paymentMethod as PaymentCreditCard;
    //
    // 		const addressFormatted = address ? {
    // 			address1: address.street,
    // 			address2: address.unit,
    // 			city: address.city,
    // 			state: address.state,
    // 			country: address.country,
    // 			zip: address.zip
    // 		} : {};
    //
    // 		return {
    // 			id,
    // 			type: paymentType,
    // 			method: accountNumber.includes('*') ? cardType : getCardType(accountNumber),
    // 			number: accountNumber,
    // 			cvv: cardCode,
    // 			expiration: expMonth + `${expYear}`.slice(-2),
    // 			contact,
    // 			email,
    // 			phone: phone as string,
    // 			address: addressFormatted,
    // 			name: nameOnCard,
    // 			description
    // 		};
    // 	}
    // 	else {
    // 		const {
    // 			id,
    // 			paymentType, // "bank" in this case
    // 			contact, // The Contact object
    // 			routingNumber,
    // 			accountNumber,
    // 			accountType, // CHECKING or SAVINGS
    // 			accountHolderType, // individual or business
    // 			description, // User-supplied description
    // 			name, // first name
    // 			surname, // last name
    // 			email, // email to send notifications to
    // 			phone, // phone number on file
    // 			address // address1, address2, city, state_or_province, postal_code, country_code
    // 		} = paymentMethod as PaymentBankAccount;
    //
    // 		const addressFormatted = address ? {
    // 			address1: address.street,
    // 			address2: address.unit,
    // 			city: address.city,
    // 			state: address.state,
    // 			country: address.country,
    // 			zip: address.zip
    // 		} : {};
    //
    // 		// Figure out bank account type
    // 		const i = accountHolderType === 'Individual';
    // 		const c = accountType === 'Checking';
    // 		let method: EBankPaymentMethodNames;
    // 		if (i && c) method = EBankPaymentMethodNames.IndividualChecking;
    // 		else if (i && !c) method = EBankPaymentMethodNames.IndividualSavings;
    // 		else if (!i && c) method = EBankPaymentMethodNames.BusinessChecking;
    // 		else method = EBankPaymentMethodNames.BusinessSavings;
    //
    // 		return {
    // 			id,
    // 			type: paymentType,
    // 			method,
    // 			number: accountNumber,
    // 			routing: routingNumber,
    // 			contact,
    // 			email,
    // 			phone,
    // 			address: addressFormatted,
    // 			name: `${name || ''} ${surname || ''}`.trim(),
    // 			description
    // 		};
    // 	}
    // }

    /**
     * Create a payment using the given payment method for the given amount.
     *
     * @param {string} paymentMethodId - payment method ID from the provider
     * @param {number} amount - amount of the transaction.  Debits will be positive numbers, credits will be negative!
     * @param {string} accountId - the account in which to create the charge
     * @param {Transaction} transaction - the transaction being processed
     */
    static async createPayment(paymentMethodId: string, amount: number, accountId: string, transaction: Transaction): Promise<Result<PayrixTransactionFull, Error>> {
        return payments.createPayment(paymentMethodId, amount, accountId, transaction);
    }

    /**
     * Fully refund a transaction.
     *
     * @param {string} transactionId
     * @param {number} amount - amount in $.cc
     * @param {string} refundKey - UUID naming this refund, the same on every retry of it
     * @returns {Promise<{txn: object, refund: object}>}
     */
    static async refundTransaction(transactionId: string, amount: number, refundKey: string): Promise<PayrixTransaction> {
        return payments.refundTransaction(transactionId, amount, refundKey);
    }

    /**
     * Void a transaction.
     *
     * @param {string} transactionId
     * @param {string} refundKey - UUID naming the refund this void can become, the same on every retry of it
     * @returns {Promise<*>}
     */
    static async voidTransaction(transactionId: PayrixEntity, refundKey: string): Promise<PayrixTransaction> {
        return payments.voidTransaction(transactionId, refundKey);
    }

    /**
     * Get details on a transaction.
     *
     * @param {PayrixEntity} transactionId
     * @returns {Promise<PayrixTransaction>}
     */
    static async getTransaction(transactionId: PayrixEntity): Promise<PayrixTransaction> {
        return payments.getTransaction(transactionId);
    }

    /**
     * Get all transactions for a given date range.
     *
     * @param {Date} startDate
     * @param {Date} endDate
     */
    static async getTransactions(startDate: Date, endDate: Date) {
        return payments.getTransactions(startDate, endDate);

        // // We will process the results to a standardized format
        // const processedResults = [];
        //
        // for (let i = 0; i < results.length; i++) {
        //     const tr = results[i];
        //     const pm: PayrixPaymentMethod = {};
        //     const account = accounts.find(a => a.id === tr.account_id);
        //
        //     pm.accountName = account.name;
        //     pm.accountNumber = account.theirId;
        //     pm.isTrust = account.trustAccount;
        //     pm.clientId = tr.client_id;
        //     pm.status = tr.status;
        //     pm.payerName = tr.method.name;
        //     pm.payerEmail = tr.method.email;
        //     pm.meta = tr.processorPaymentMeta;
        //
        //     // Find the related contact
        //     if (tr.method.email) {
        //     	pm.contacts = store.getState().resources
        //     		.filter(r => r.type === 'email' && r.value === tr.method.email)
        //     		.map(r => store.getState().contacts.find(c => c.id === r.entityId));
        //     }
        //     else pm.contacts = [];
        //
        //     processedResults.push(pm);
        // }
        //
        // return processedResults;
    }

    static getPaymentMethodName(paymentMethod: EPaymentMethods) {
        return PaymentMethodNames[paymentMethod];
    }

    static getTxnResultColdeName(txnResult: PayrixTxnResult) {

    }

    /**
     * Parse out fields we care about from a payment/void/refund, specifically:
     * - authorization/_code: for display to the user
     * - id: the ID of the transaction, for later lookup
     * - status: e.g. VOIDED, AUTHORIZED, PENDING, COMPLETED, ACTIVE
     * - type: e.g. CHARGE, REFUND, CREDIT (note that void returns CHARGE)
     * - amount: the amount of the charge
     * - amount_refunded: the amount refunded (if a refund)
     * - chargeDate: the date the payment method was initially charged
     * - voidOrRefundDate: the date the payment was (last) voided or refunded
     *
     * @param {PayrixTransactionFull} txn
     * @param {Transaction} [tr] - optional transactionID, useful for defaulting bad description data
     * @returns {PayrixPaymentMeta}
     */
    static parsePaymentMeta(txn: PayrixTransactionFull, tr?: Transaction): PayrixPaymentMeta {
        let id = txn.id;
        let type = txn.type;
        let payment = txn.payment;
        let status = txn.status;
        let authorization = txn.authorization;
        let approved = txn.approved;
        let total = txn.total;
        let settledTotal = txn.settledTotal;
        let refunded = txn.refunded;
        let created = txn.created;
        let modified = txn.modified;
        let batch = txn.batch;

        if (!created) {
            console.error(`No creation date for this transaction, using now()`);
            created = new Date().toISOString().replace('T', ' ').replace('Z', '');
        }

        if (!modified) {
            console.error(`No creation date for this transaction, using now()`);
            modified = new Date().toISOString().replace('T', ' ').replace('Z', '');
        }

        let transactionType: ETransactionTypeName;
        switch (type) {
            case ETransactionType.CreditCardSale:
                transactionType = ETransactionTypeName.CreditCardSale;
                break;
            case ETransactionType.CreditCardAuth:
                transactionType = ETransactionTypeName.CreditCardAuth;
                break;
            case ETransactionType.CreditCardCapture:
                transactionType = ETransactionTypeName.CreditCardCapture;
                break;
            case ETransactionType.CreditCardReverseAuth:
                transactionType = ETransactionTypeName.CreditCardReverseAuth;
                break;
            case ETransactionType.CreditCardRefund:
                transactionType = ETransactionTypeName.CreditCardRefund;
                break;
            case ETransactionType.ECheckSale:
                transactionType = ETransactionTypeName.ECheckSale;
                break;
            case ETransactionType.ECheckRefund:
                transactionType = ETransactionTypeName.ECheckRefund;
                break;
            case ETransactionType.ECheckRedeposit:
                transactionType = ETransactionTypeName.ECheckRedeposit;
                break;
            case ETransactionType.ECheckAccountVerification:
                transactionType = ETransactionTypeName.ECheckAccountVerification;
                break;
        }

        let currentStatus: ETransactionStatusName;
        if ([ETransactionType.CreditCardSale, ETransactionType.ECheckSale].includes(type) && batch === null)
            currentStatus = ETransactionStatusName.Voided;
        else switch (status) {
            case ETransactionStatus.Pending:
                currentStatus = ETransactionStatusName.Pending;
                break;
            case ETransactionStatus.Approved:
                currentStatus = ETransactionStatusName.Approved;
                break;
            case ETransactionStatus.Failed:
                currentStatus = ETransactionStatusName.Failed;
                break;
            case ETransactionStatus.Captured:
                currentStatus = ETransactionStatusName.Captured;
                break;
            case ETransactionStatus.Settled:
                currentStatus = ETransactionStatusName.Settled;
                break;
            case ETransactionStatus.Returned:
                currentStatus = ETransactionStatusName.Returned;
                break;
        }

        // OWA-593 Bug B: txn.payment is optional/nullable — guard the deref so a null
        // payment (e.g. an account-verification txn) no longer crashes, matching the already
        // null-safe number/routing handling below. OWA-944: an absent method takes card or
        // bank from the transaction type (card types are 1-5), as the server parser does, so
        // a card sale is not relabelled Bank. The `!`s are type-only: these helpers all
        // tolerate an undefined runtime value (out-of-range number → not-a-card / Err).
        const method = txn.payment?.method;
        const isCard = method != null
            ? isCreditCard(method)
            : txn.type >= ETransactionType.CreditCardSale && txn.type <= ETransactionType.CreditCardRefund;

        let rCardType = isCard ? getCardTypeName(method!) : null;
        let rAccountType = isCard ? null : getBankTypeName(method!); // TODO: Errors here?
        let cardType = rCardType?.isOk() ? rCardType.unwrap() : null;
        let accountType = rAccountType?.isOk() ? rAccountType.unwrap() : null;

        // OWA-944: callers save this parse over the stored metadata. The card or bank details
        // of a Payrix charge never change, so a refetch of the SAME charge that lacks them
        // keeps the stored ones (server twin: storedPaymentMethod.ts). `tr` may be absent or
        // not a Transaction on the paymentApi path; the optional chain then finds nothing.
        const stored = tr?.processorPaymentMeta?.id === id ? tr.processorPaymentMeta.method : undefined;
        const freshNumber = txn.payment?.number != null ? `${txn.payment.number}` : '';
        const freshRouting = txn.payment?.routing != null ? `${txn.payment.routing}` : null;
        const freshFingerprint = typeof txn.token === 'string' ? txn.token : txn.token?.token;

        const result = {
            id, // Payrix ID for the transaction
            merchant: txn.merchant, // ID of the merchant
            type,
            typeText: transactionType,
            // OWA-593 Bug A: tr is optional and the paymentApi path reaches here with it
            // undefined (or a PaymentMethod), so guard the fallback rebuild. `tr?.id` was
            // already guarded; `tr?.txType`/`tr?.notes` match it. An absent txType is not a
            // trust type, an absent note is ''. (Type-confusion of the tr arg on the
            // paymentApi path is a noted follow-up; this only stops the crash.)
            description: txn.description || JSON.stringify({id: tr?.id || getZeroUUID(), isTrust: ['FR', 'TT', 'PT'].includes(tr?.txType ?? ''), description: tr?.notes || ''}),
            status,
            statusText: currentStatus,
            authorization,
            // Payrix carries integer cents; `cents / 100` is exact at scale 2
            // for those inputs, so the legacy toTwoDecimals wrappers were
            // no-ops. Removed in OWA-451 for consistency with the broader
            // "no mid-calc rounding" discipline.
            amount: (settledTotal || approved || total) / 100,
            amount_refunded: (refunded ?? 0) / 100,
            chargeDate: convertEasternToUTC(created),
            voidOrRefundDate: batch === null || (refunded ?? 0) > 0 ? convertEasternToUTC(modified) : null, // have to find this out other ways
            method: {
                type: isCard ? EPaymentType.Card : EPaymentType.Bank, // 1-7 are cards, 8-12 are bank accounts
                // OWA-231: see parsePaymentMeta — never coerce an absent value to
                // the string "undefined" (rendered as "ined" via .slice(-4)).
                number: freshNumber || stored?.number || '',
                routing: freshRouting ?? stored?.routing ?? null,
                cardType: cardType ?? stored?.cardType ?? null,
                accountType: accountType ?? stored?.accountType ?? null,
                // OWA-714: was `txn.token?.token`, which is always undefined —
                // Payrix returns `token` as the bare token string on /txns and
                // the expand that would nest it does not work. Same bug, and
                // same fix, as the server's parsePaymentMeta.
                fingerprint: freshFingerprint ?? stored?.fingerprint
            },
            ipCreated: txn.ipCreated,
            // OWA-714: `subscription` has the same expanded-vs-bare ambiguity as
            // `token`. Unlike `token` its bare shape is not verified against the
            // sandbox, so accept either rather than assume the expand works.
            subscription: typeof txn.subscription === 'string' ? txn.subscription : txn.subscription?.id,

            // Note: these are date strings, not amounts!
            captured: txn.captured,
            settled: txn.settled,
            funded: txn.funded,

            // These are numbers (integer cents → dollars; no rounding here).
            total: txn.total / 100,
            settledTotal: (txn.settledTotal ?? 0) / 100,
            refunded: (txn.refunded ?? 0) / 100,
            approved: (txn.approved ?? 0) / 100,

            inactive: 0,
            frozen: 0,

            payment
        } as PayrixPaymentMeta;

        const parsed = ZPayrixPaymentMeta.safeParse(result);
        if (!parsed.success) {
            console.warn('parsePaymentMeta struct is not correct', parsed.error.message);
        }

        return result;
    }

    /**
     * Get the current status of a transaction.
     *
     * This will return an array of status flags.  Specifically:
     *
     * - NONE: no processor action is associated with this transaction.
     * - INCOMPLETE:  the transaction is in progress, and can be voided.
     * - COMPLETED: the transaction has been settled, and can be refunded but not voided.
     * - VOID: the transaction has been voided.  This is immediate, so once voided nothing can be done.
     * - REFUND: the transaction is being/has been refunded.  Check for INCOMPLETE vs COMPLETED.
     * - CHARGE: the transaction is being/has been processed as a charge.  Check for INCOMPLETE vs COMPLETED.
     *
     * Valid combinations are:
     * - []
     * - [CHARGE, INCOMPLETE]
     * - [CHARGE, COMPLETE]
     * - [VOID, COMPLETE]
     * - [REFUND, INCOMPLETE]
     * - [REFUND, COMPLETE]
     *
     * Accepts EITHER a raw `PayrixTransaction` straight off the API, OR one of our
     * own `Transaction` entities, which carries the already-parsed processor meta
     * on `processorPaymentMeta`. Both are real callers: PaymentActionBox passes the
     * API transaction, PaymentCard passes the entity. For an entity, the five inputs
     * are read from the normalized meta in the units it already carries: `amount`
     * and `amount_refunded` are dollars from both the Payrix and the LawPay parser
     * (`total` is not: it is dollars from one and cents from the other), and a void
     * is `statusText === Voided`, which is how `parsePaymentMeta` records a null batch.
     *
     * That second form is why this function is not `PayrixTransaction`-only (OWA-843).
     * `paymentApi.getCurrentStatus` fans out to two delegates that disagreed about
     * their argument: LawPay's reads `tr.processorPaymentMeta` (an entity), Payrix's
     * read a raw API transaction. PaymentCard was written against the LawPay shape,
     * so under Payrix every field this function reads was undefined: `batch === null`
     * was `undefined === null` (false, so a void never produced VOID) and the numeric
     * `type`/`status` were absent, so it fell through to CHARGE plus INCOMPLETE. The
     * visible damage was a voided payment that kept displaying as live in the
     * Transactions ledger, because the ledger reads `Transaction.voidedDate` and the
     * VOID flag is what stamps it.
     *
     * @returns {flags: Flags[], message: string} - an array of flags indicating current status.
     */
    static getCurrentStatus(txn: PayrixTransaction | Transaction): { flags: Flags[], message: string } {
        if (!txn) return {flags: [], message: ''};

        const meta = normalizePaymentMeta((txn as Transaction).processorPaymentMeta);
        const raw = txn as PayrixTransaction;
        const type = meta ? meta.type : raw.type;
        const status = meta ? meta.status : raw.status;
        const refunded = meta ? (meta.amount_refunded ?? 0) : (raw.refunded ?? 0);
        const isVoided = meta ? meta.statusText === ETransactionStatusName.Voided : raw.batch === null;
        const amt = util.formatCurrency(meta ? meta.amount : raw.total / 100, true);

        const state = status === ETransactionStatus.Pending ? 'is pending.' :
                      status === ETransactionStatus.Captured ? 'has been captured.' :
                      status === ETransactionStatus.Approved ? 'has been approved.' : '';

        const STATUS = [ETransactionStatus.Failed, ETransactionStatus.Returned].includes(status)
                       ? 'FAILED'
                       : [ETransactionStatus.Pending, ETransactionStatus.Captured, ETransactionStatus.Approved].includes(status)
                         ? 'INCOMPLETE'
                         : 'COMPLETE';

        // Easy case: if the transaction has anything in the refunded field, it was a refunded.
        // The status is then just whatever the status is in the txn.status field
        if ([ETransactionType.CreditCardRefund, ETransactionType.ECheckRefund].includes(type) || refunded > 0) return {
            flags: ['REFUND', STATUS],
            message: `Refund of ${amt} is ${getStatusText(status).toLowerCase()}.`
        }

        // Easy case: if the batch is null, then the transaction was successfully canceled
        if (isVoided) return {
            flags: ['VOID', STATUS],
            message: `Payment of ${amt} has been voided.`
        }

        // If the transaction is not canceled, we'll need to determine its current status.
        if ([ETransactionType.CreditCardSale, ETransactionType.ECheckSale].includes(type)) {
            if ([ETransactionStatus.Pending, ETransactionStatus.Captured, ETransactionStatus.Approved].includes(status)) {
                return {
                    flags: ['CHARGE', 'INCOMPLETE'],
                    message: `Payment of ${amt} ${state}`
                }
            }
            else if (status === ETransactionStatus.Settled) return {
                flags: ['CHARGE', 'COMPLETE'],
                message: `Payment of ${amt} has settled.`
            }
            else if ([ETransactionStatus.Failed, ETransactionStatus.Returned].includes(status)) return {
                flags: ['CHARGE', 'FAILED'],
                message: `Payment of ${amt} failed.`
            }
            else throw Error(`Unknown type/status received: '${type}/${status}.'`);
        }
            // I think these all create new transactions, which we won't see, so do we need them?
            // else if ([ETransactionType.CreditCardReverseAuth, ETransactionType.CreditCardRefund, ETransactionType.ECheckRefund].includes(txn.type)) {
            //     if (status === ETransactionStatus.Returned) return {
            //         flags: ['REFUND', 'COMPLETE'],
            //         message: `Refund of ${amt} is complete.`
            //     };
            //     else return {
            //         flags: ['REFUND', 'INCOMPLETE'],
            //         message: `Refund of ${amt} has been initiated.`
            //     }
        // }
        else {
            return {
                flags: ['CHARGE', 'INCOMPLETE'],
                message: `Payment of ${amt} has been submitted, waiting for funds to settle.`
            }
        }
    }

    /**
     * Get the completion date from the metadata.
     *
     * @param {object} meta
     * @return {Date|null}
     */
    static getCompletionDate(meta) {
        return meta?.completion_date;
    }
}

export default Payrix;
